import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus, Sparkles, ChevronRight } from "lucide-react";
import { PRODUCTS } from "../lib/brand";
import Reveal from "../components/Reveal";
import CTAStrip from "../components/CTAStrip";

const COMPARE_ROWS = [
  ["Billing", true, true, false, true, false],
  ["Inventory", true, true, false, true, false],
  ["Multi-branch", true, false, false, false, false],
  ["Cloud sync", true, true, true, true, true],
  ["Owner monitoring", false, false, true, false, false],
  ["Van sales", false, false, false, true, false],
  ["Delivery tracking", false, false, false, false, true],
  ["Android app", false, true, true, true, true],
  ["GST compliance", true, true, false, true, false],
  ["Tally integration", true, false, false, false, false],
  ["WhatsApp billing", true, true, false, false, false],
];

const Cell = ({ v }) =>
  v === true ? (
    <span className="inline-flex h-7 w-7 rounded-full bg-[#10B981]/15 text-[#10B981] items-center justify-center">
      <Check className="h-4 w-4" />
    </span>
  ) : (
    <span className="inline-flex h-7 w-7 rounded-full bg-[#DDE6F0] text-[#6B7280] items-center justify-center">
      <Minus className="h-4 w-4" />
    </span>
  );

export default function DigiposHub() {
  return (
    <div data-testid="digipos-hub-page">
      {/* HERO */}
      <section className="relative gi-hero-gradient text-white pt-32 pb-20">
        <div className="absolute inset-0 gi-mesh opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <div className="flex justify-center items-center gap-2 text-white/60 text-sm mb-4">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-white">DigiPOS</span>
            </div>
            <span className="gi-pill-dark"><Sparkles className="h-3.5 w-3.5" /> The Complete Suite</span>
            <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mt-5 leading-tight">
              One Platform. <span className="bg-gradient-to-r from-[#00B4D8] to-white bg-clip-text text-transparent">Complete</span> Business Control.
            </h1>
            <p className="text-white/75 mt-5 max-w-2xl mx-auto text-lg">
              DigiPOS is a suite of 5 integrated products — choose the one that fits your business, or use them all together.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/80">
              <span>3,000+ businesses</span>
              <span>·</span>
              <span>In-house developed</span>
              <span>·</span>
              <span>Offline + Online</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section className="bg-[#F5F8FF] gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.slug} delay={i * 70}>
              <div className="gi-card gi-card-hover p-7 h-full flex flex-col" style={{ borderTop: `4px solid ${p.accent}` }}>
                <div className="h-12 w-12 rounded-xl grid place-items-center mb-4" style={{ background: `${p.accent}15`, color: p.accent }}>
                  <Sparkles className="h-5 w-5" />
                </div>
                <h3 className="font-display font-bold text-xl">{p.name}</h3>
                <p className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-3">{p.tag}</p>
                <ul className="space-y-2 text-sm text-[#1A1A2E]/85 mt-2 mb-5">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: p.accent }} /> {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-4 border-t border-[#DDE6F0]">
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {p.platforms.map((pl) => (
                      <span key={pl} className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-[#F5F8FF] text-[#6B7280]">
                        {pl}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={`/digipos/${p.slug}`}
                    data-testid={`hub-explore-${p.slug}`}
                    className="inline-flex items-center gap-1.5 font-medium text-sm hover:gap-2.5 transition-all"
                    style={{ color: p.accent }}
                  >
                    Explore {p.name.replace("DigiPOS ", "")} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-3">Which product is right for you?</h2>
            <p className="text-[#6B7280] max-w-2xl">Compare features across all 5 DigiPOS products at a glance.</p>
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-10 gi-card overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="bg-[#F5F8FF]">
                    <th className="text-left p-5 text-sm font-semibold text-[#0B2545]">Feature</th>
                    {PRODUCTS.map((p) => (
                      <th key={p.slug} className="p-5 text-sm font-semibold text-center" style={{ color: p.accent }}>
                        {p.name.replace("DigiPOS ", "")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARE_ROWS.map((row, idx) => (
                    <tr key={idx} className="border-t border-[#DDE6F0]">
                      <td className="p-4 text-sm font-medium">{row[0]}</td>
                      {row.slice(1).map((v, i) => (
                        <td key={i} className="p-4 text-center"><Cell v={v} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <CTAStrip
        heading="Not sure which product fits your business?"
        sub="Our team will walk you through a live demo tailored to your industry and size."
      />
    </div>
  );
}
